import React from 'react';
import { Dropdown, DropdownItemProps, DropdownProps } from 'semantic-ui-react';

export interface RidershipNamedEntity {
    displayName: string;
}

interface DropdownItem<T extends RidershipNamedEntity> {
    text: React.ReactNode;
    value: number;
    external: T;
}

interface BaseDropdownProps<T extends RidershipNamedEntity> {
    loading?: boolean;
    disabled?: boolean;
    items: T[] | null;
    value: T | null;
    onChanged: (value: T | null) => void;
    placeholder?: string;
    clearable?: boolean;
    search?: boolean;
    renderItem?: (item: T) => React.ReactNode;
    className?: string;
}

export function RouteSelect<T extends RidershipNamedEntity>(props: BaseDropdownProps<T>) {
    const [items, setItems] = React.useState<DropdownItem<T>[]>([]);
    const [selected, setSelected] = React.useState<number | string>();

    React.useEffect(() => {
        const mapped = (props.items || []).map((item, index) => {
            const text = props.renderItem ? props.renderItem(item) : item.displayName;
            return { text, value: index, external: item };
        });
        const selectedIndex = mapped.findIndex(item => item.external === props.value);
        setItems(mapped);
        setSelected(selectedIndex < 0 ? '' : selectedIndex);
    }, [props.items, props.value, props.renderItem]);

    const handleChanged = (_event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        if (typeof data.value === 'number') {
            setSelected(data.value);
            props.onChanged(items[data.value].external);
        } else {
            setSelected('');
            props.onChanged(null);
        }
    };

    const clearable = props.clearable ?? false;
    return (
        <Dropdown
            className={props.className}
            placeholder={props.placeholder ?? 'Select route'}
            loading={!!props.loading}
            disabled={props.disabled}
            fluid
            search={props.search !== false}
            selection
            options={items}
            value={selected}
            onChange={handleChanged}
            clearable={clearable}
            icon={clearable && typeof selected === 'number' ? 'delete' : undefined}
            openOnFocus={false}
            selectOnBlur={false}
            selectOnNavigation={false}
        />
    );
}

export function DirectionSelect<T extends RidershipNamedEntity>(props: BaseDropdownProps<T>) {
    const [items, setItems] = React.useState<DropdownItem<T>[]>([]);
    const [selected, setSelected] = React.useState<number | string>();

    React.useEffect(() => {
        const mapped = (props.items || []).map((item, index) => {
            const text = props.renderItem ? props.renderItem(item) : item.displayName;
            return { text, value: index, external: item };
        });
        const selectedIndex = mapped.findIndex(item => item.external === props.value);
        setItems(mapped);
        setSelected(selectedIndex < 0 ? '' : selectedIndex);
    }, [props.items, props.value, props.renderItem]);

    const handleChanged = (_event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        if (typeof data.value === 'number') {
            setSelected(data.value);
            props.onChanged(items[data.value].external);
        } else {
            setSelected('');
            props.onChanged(null);
        }
    };

    const clearable = props.clearable ?? false;
    return (
        <Dropdown
            className={props.className}
            placeholder={props.placeholder ?? 'Direction'}
            loading={!!props.loading}
            disabled={props.disabled}
            fluid
            search={props.search !== false}
            selection
            options={items}
            value={selected}
            onChange={handleChanged}
            clearable={clearable}
            icon={clearable && typeof selected === 'number' ? 'delete' : undefined}
            openOnFocus={false}
            selectOnBlur={false}
            selectOnNavigation={false}
        />
    );
}

function toMessage(message: string | undefined) {
    if (!message) return message;
    const lines = message.trim().split('\n');
    if (lines.length < 2) return message;
    return (
        <>
            {lines.map((line, index) => (
                <React.Fragment key={index}>
                    {index === 0 ? <b>{line}</b> : line}
                    <br />
                </React.Fragment>
            ))}
        </>
    );
}

interface StopsDropdownProps<T extends RidershipNamedEntity> extends BaseDropdownProps<T> {
    noResultsMessage?: string;
    onSearchChanged: (searchQuery: string) => void;
    searchQuery?: string;
}

export function StopSelect<T extends RidershipNamedEntity>(props: StopsDropdownProps<T>) {
    const [items, setItems] = React.useState<DropdownItem<T>[]>([]);
    const [selected, setSelected] = React.useState<number | undefined>(undefined);
    const identitySearch = React.useCallback(
        (dropdownOptions: DropdownItemProps[], _searchQuery: string) => dropdownOptions,
        [],
    );

    React.useEffect(() => {
        const mapped = (props.items || []).map((item, index) => {
            const text = props.renderItem ? props.renderItem(item) : item.displayName;
            return { text, value: index, external: item };
        });
        const selectedIndex = mapped.findIndex(item => item.external === props.value);
        setItems(mapped);
        setSelected(selectedIndex < 0 ? undefined : selectedIndex);
    }, [props.items, props.value, props.renderItem]);

    const handleChanged = (_event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        if (typeof data.value === 'number') {
            setSelected(data.value);
            props.onChanged(items[data.value].external);
        } else {
            setSelected(undefined);
            props.onChanged(null);
        }
    };

    const clearable = props.clearable ?? true;
    const placeholder = props.placeholder ?? 'Start typing name or ID of a stop';
    const noResults = props.loading
        ? 'Searching...'
        : toMessage(props.noResultsMessage) || placeholder;

    return (
        <Dropdown
            className={props.className}
            placeholder={placeholder}
            loading={!!props.loading}
            disabled={props.disabled}
            fluid
            // Avoid client-side filtering so stop IDs returned from the server remain visible.
            search={props.search === false ? false : identitySearch}
            searchQuery={props.searchQuery}
            selection
            options={items}
            value={selected ?? undefined}
            onChange={handleChanged}
            clearable={clearable}
            icon={clearable && typeof selected === 'number' ? 'delete' : undefined}
            onSearchChange={(_event, data) => props.onSearchChanged(String(data.searchQuery ?? ''))}
            openOnFocus={false}
            selectOnBlur={false}
            selectOnNavigation={false}
            noResultsMessage={noResults}
        />
    );
}

// Simple Dropdown for Trips (All trips)
interface SimpleOption { key: string; value: string; text: string; }

interface TripDropdownProps {
    loading?: boolean;
    disabled?: boolean;
    items: SimpleOption[];
    value: string | null | undefined;
    onChanged: (value: string | null) => void;
    placeholder?: string;
    clearable?: boolean;
    search?: boolean;
    className?: string;
}

export function TripSelect(props: TripDropdownProps) {
    const selected = props.value ?? '';
    const options = props.items || [];

    const handleChanged = (_event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const v = (data.value as string) || '';
        props.onChanged(v || null);
    };

    const clearable = props.clearable ?? true;
    const placeholder = props.placeholder ?? 'All trips';

    return (
        <Dropdown
            className={props.className}
            placeholder={placeholder}
            loading={!!props.loading}
            disabled={props.disabled}
            fluid
            search={props.search !== false}
            selection
            options={options}
            value={selected || undefined}
            onChange={handleChanged}
            clearable={clearable}
            icon={clearable && selected ? 'delete' : undefined}
            openOnFocus={false}
            selectOnBlur={false}
            selectOnNavigation={false}
        />
    );
}
